// Pantalla TFT ILI9341 de 2.8″ en 3D. El panel es vertical (240×320) y va montado en horizontal en el
// módulo, así que la imagen física se traspone para dibujarla sobre la placa.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape } from '../kit.js';
import { TFT } from '../../parts/tft.js';
import { getPins } from '../../parts/registry.js';

let silk = null;

function drawSilk(pins) {
  const { c, g } = canvas(TFT.w, TFT.d, 12);
  const X = (x) => x + TFT.w / 2;
  const Y = (z) => z + TFT.d / 2;
  g.fillStyle = '#b3201b';
  g.fillRect(0, 0, TFT.w, TFT.d);
  for (const p of pins) text(g, { MOSI: 'SDI(MOSI)', MISO: 'SDO(MISO)', DC: 'D/C', RST: 'RESET' }[p.name] || p.name, X(p.x) - 2.4, Y(p.z), { size: 1.2, color: '#f4e9e6', weight: 700, align: 'right' });
  text(g, '2.8" TFT SPI 240x320', X(-6), Y(TFT.d / 2 - 1.8), { size: 1.6, color: '#f4e9e6', weight: 700 });
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

export function buildTft(def, props) {
  const pins = getPins(def.type, props);
  if (!silk) silk = drawSilk(pins);
  const b = new Builder();
  const top = TFT.pcbY + TFT.t;
  const holes = [[-40, -22, 1.5], [40, -22, 1.5], [-40, 22, 1.5], [40, 22, 1.5]];
  b.root.add(extrudeBoard(boardShape(TFT.w, TFT.d, { r: 1.2 }), TFT.t, silk, '#8f1915', { holes, w: TFT.w, d: TFT.d, y0: TFT.pcbY }));
  for (const [x, z] of holes) b.cyl(1.9, 1.9, TFT.pcbY, M.steel, x, 0, z, 12);
  const [pw, pd] = TFT.panel;
  b.box(pw, 2.4, pd, mat('#111317', { roughness: 0.35, metalness: 0.3 }), TFT.panelX, top, 0);
  b.box(pw - 2, 0.3, 3, mat('#d9dce0', { roughness: 0.4, metalness: 0.6 }), TFT.panelX, top + 2.4, -pd / 2 + 1.6);
  // Conector de 9 pines en el borde derecho
  const x = TFT.w / 2 - 3;
  b.box(2.54, 2.5, pins.length * 2.54, M.blackPlastic, x, top, 0);
  for (const p of pins) b.box(0.64, 8.6, 0.64, M.gold, x, top, p.z);
  b.build();

  const [aw, ad] = TFT.active;
  const cv = document.createElement('canvas');
  cv.width = 320;
  cv.height = 240;
  const g = cv.getContext('2d');
  const img = g.createImageData(320, 240);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.anisotropy = 8;
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(aw, ad).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: tex }));
  screen.position.set(TFT.panelX - 2, top + 2.42, 1.2);
  b.root.add(screen);
  let fb = null;
  const paint = (v) => {
    const d = img.data;
    if (!v || !v.bl) {
      g.fillStyle = '#05070a';
      g.fillRect(0, 0, 320, 240);
    } else if (!v.on || !fb) {
      g.fillStyle = '#d4d8de';
      g.fillRect(0, 0, 320, 240);
    } else {
      for (let py = 0; py < 320; py++) {
        for (let px = 0; px < 240; px++) {
          const c = fb[py * 240 + px];
          const i = (px * 320 + py) * 4;
          let r = (((c >> 11) & 0x1f) * 527 + 23) >> 6;
          let gr = (((c >> 5) & 0x3f) * 259 + 33) >> 6;
          let bl = ((c & 0x1f) * 527 + 23) >> 6;
          if (v.invert) {
            r = 255 - r;
            gr = 255 - gr;
            bl = 255 - bl;
          }
          d[i] = r;
          d[i + 1] = gr;
          d[i + 2] = bl;
          d[i + 3] = 255;
        }
      }
      g.putImageData(img, 0, 0);
    }
    tex.needsUpdate = true;
  };
  paint(null);
  return {
    root: b.root,
    update(v) {
      if (v.fb) fb = v.fb;
      paint(v);
    },
    reset() {
      fb = null;
      paint(null);
    },
    dispose() {
      tex.dispose();
    },
  };
}

export const MODELS = { 'tft-ili9341': buildTft };
